/* eslint-disable jsx-a11y/anchor-is-valid */
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ExternalLink,
  Funnel,
  Tags,
  Trash2,
} from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { calculateSignupDate } from "./utils";
import { HEALTH_INDEX_OPTIONS } from "../../../config";
import { CategoryData } from "../../../models/CategoryData";
import { FacilityManagementFilterConditionData } from "../../../models/FacilityFilterConditionData";
import { InternalLabelData } from "../../../models/InternalLabelData";
import { LocationData } from "../../../models/LocationData";
import { OrganizationData } from "../../../models/OrganizationData";
import DataSvc from "../../../services/dataSvc";
import ConfigurableTableLayout from "../../Common/ConfigurableTableLayout";
import { DemoBadge } from "../../Common/DemoBadge";
import { FacilityIssuesIcon } from "../../Common/FacilityIssuesIcon";
import OverflowBadge from "../../Common/OverflowBadge";
import TableRowActions, {
  TableRowActionItem,
} from "../../Common/TableRowActions";
import ColumnFilterPopover from "../../FacilityListComponents/RefactoredFacilityTable/ColumnFilterPopover";
import ScoreItem from "../../ScoreItem";
import ListTopBar from "../ListTopBar";
import ModalAssignInternalLabels from "../ModalAssignInternalLabels";
import ModalDeleteAccount from "../ModalDeleteAccount";

import "./styles.scss";

export interface IFacilityManagementListProps {
  dataList: LocationData[];
  totalCount: number;
  conditionFormData: FacilityManagementFilterConditionData;
  filterFormData: any;
  organizationId: string;
  onClickFilter: () => void;
  onClickClearFilterCondition: () => void;
  onApplyFilterCondition: (
    conditionFormData: FacilityManagementFilterConditionData
  ) => void;
  onChangeFilterFormData: (filterFormData: any) => void;
  onClickAssignOrg: (facilityItem: LocationData) => void;
  onUpdateAssignLabels: (
    locationId: number,
    addIds: string[],
    deleteIds: string[]
  ) => Promise<void>;
  onDeleteAccountComplete?: () => void;
}

type ManagementColumnId =
  | "name"
  | "organizations"
  | "has_loc_admins"
  | "date_signup_completed"
  | "prefecture"
  | "city"
  | "category"
  | "is_archived"
  | "health_index"
  | "internal_labels"
  | "actions";

type SortOrder = "asc" | "desc";

type SortableField =
  | "count.organizations"
  | "name"
  | "prefecture"
  | "city"
  | "has_loc_admins"
  | "stats.score"
  | "data_last_collected_at"
  | "internal_labels";

const ALL_COLUMNS: ManagementColumnId[] = [
  "name",
  "organizations",
  "has_loc_admins",
  "date_signup_completed",
  "prefecture",
  "city",
  "category",
  "is_archived",
  "health_index",
  "internal_labels",
  "actions",
];

const DEFAULT_VISIBLE_COLUMNS: ManagementColumnId[] = [...ALL_COLUMNS];

const VISIBLE_COLUMNS_STORAGE_KEY =
  "FacilityManagementPage_RefactoredVisibleColumns";

const SORT_LABELS: Record<SortableField, { asc: string; desc: string }> = {
  "count.organizations": {
    asc: "振り分け状況 ↑",
    desc: "振り分け状況 ↓",
  },
  name: { asc: "施設名 ↑", desc: "施設名 ↓" },
  prefecture: { asc: "都道府県 ↑", desc: "都道府県 ↓" },
  city: { asc: "市区町村 ↑", desc: "市区町村 ↓" },
  has_loc_admins: { asc: "施設ユーザー狀況 ↑", desc: "施設ユーザー狀況 ↓" },
  "stats.score": { asc: "健康指数 ↑", desc: "健康指数 ↓" },
  data_last_collected_at: {
    asc: "データ最終収集日 ↑",
    desc: "データ最終収集日 ↓",
  },
  internal_labels: { asc: "Adminラベル ↑", desc: "Adminラベル ↓" },
};

const COLUMN_SORT_FIELD: Partial<Record<ManagementColumnId, SortableField>> = {
  name: "name",
  organizations: "count.organizations",
  has_loc_admins: "has_loc_admins",
  date_signup_completed: "data_last_collected_at",
  prefecture: "prefecture",
  city: "city",
  health_index: "stats.score",
  internal_labels: "internal_labels",
};

const normalizeVisibleColumns = (value: unknown): ManagementColumnId[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const migratedValue = value.flatMap((columnId) =>
    columnId === "location" ? ["prefecture", "city"] : [columnId]
  );
  const filtered = migratedValue.filter((col): col is ManagementColumnId =>
    ALL_COLUMNS.includes(col as ManagementColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const required = new Set<ManagementColumnId>(["name", "actions"]);
  unique.forEach((item) => required.add(item));

  return ALL_COLUMNS.filter((item) => required.has(item));
};

const getSortByLabel = (sort: SortableField, order: SortOrder): string => {
  return SORT_LABELS[sort][order];
};

export const FacilityManagementList: React.FunctionComponent<
  IFacilityManagementListProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) => _t(`facilityManagementPage.facilityManagementList.${key}`),
    [_t]
  );

  const {
    dataList,
    totalCount,
    conditionFormData,
    filterFormData, // organizationId,
    onClickClearFilterCondition,
    onApplyFilterCondition,
    onChangeFilterFormData,
    onClickAssignOrg,
    onUpdateAssignLabels,
  } = props;

  const [visibleColumns, setVisibleColumns] = useState<ManagementColumnId[]>(
    () => {
      const savedColumns = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
      if (!savedColumns) {
        return DEFAULT_VISIBLE_COLUMNS;
      }

      try {
        return normalizeVisibleColumns(JSON.parse(savedColumns));
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
  );
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  const [showAssignLabelsModal, setShowAssignLabelsModal] =
    React.useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] =
    React.useState(false);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(
    null
  );

  const { data: organizationsResp } = useQuery(
    ["facilityManagementTableOrganizations"],
    () =>
      DataSvc.getOrganizations({
        sort: "name",
        limit: 1000,
      })
  );

  const { data: internalLabelsResp } = useQuery(
    ["facilityManagementTableInternalLabels"],
    () =>
      DataSvc.getInternalLabels({
        sort: "id",
      })
  );

  const currentSortField =
    (filterFormData.sort as SortableField) || "count.organizations";
  const currentSortOrder: SortOrder =
    filterFormData.order === "asc" ? "asc" : "desc";

  const activeConditionChips = useMemo(() => {
    const chips: { id: string; label: string; onRemove: () => void }[] = [];

    const getSortFieldLabel = () => {
      switch (currentSortField) {
        case "count.organizations":
          return "振り分け状況";
        case "name":
          return t("facility_name");
        case "prefecture":
          return t("prefecture");
        case "city":
          return t("city");
        case "has_loc_admins":
          return t("loc_admin_present");
        case "stats.score":
          return t("health_index");
        case "data_last_collected_at":
          return "データ最終収集日";
        case "internal_labels":
          return t("internal_labels");
        default:
          return t("facility_name");
      }
    };

    chips.push({
      id: `sort-${currentSortField}`,
      label: `並び替え順: ${getSortFieldLabel()} ${
        currentSortOrder === "desc" ? "降順" : "昇順"
      }`,
      onRemove: () => {
        onChangeFilterFormData({
          ...filterFormData,
          sort: "count.organizations",
          order: "desc",
          sortBy: getSortByLabel("count.organizations", "desc"),
          pageIndex: 1,
        });
      },
    });

    if (conditionFormData.isArchived?.shouldInclude !== undefined) {
      chips.push({
        id: "is_archived",
        label: `${t("is_archived")} : ${
          conditionFormData.isArchived.shouldInclude
            ? t("archived")
            : t("not_archived")
        }`,
        onRemove: () => {
          onApplyFilterCondition({
            ...conditionFormData,
            isArchived: undefined,
          });
          onChangeFilterFormData({
            ...filterFormData,
            pageIndex: 1,
          });
        },
      });
    }

    const checkedHealthIndex = (conditionFormData.healthIndex || [])
      .filter((item) => item.checked)
      .map((item) => item.name);
    if (checkedHealthIndex.length > 0) {
      chips.push({
        id: "health_index",
        label: `${t("health_index")} : ${checkedHealthIndex.join(", ")}`,
        onRemove: () => {
          onApplyFilterCondition({
            ...conditionFormData,
            healthIndex: [],
          });
          onChangeFilterFormData({
            ...filterFormData,
            pageIndex: 1,
          });
        },
      });
    }

    if ((conditionFormData.internalLabels || []).length > 0) {
      chips.push({
        id: "internal_labels",
        label: `${t("internal_labels")} : ${conditionFormData.internalLabels
          .map((item) => item.name)
          .join(", ")}`,
        onRemove: () => {
          onApplyFilterCondition({
            ...conditionFormData,
            internalLabels: [],
          });
          onChangeFilterFormData({
            ...filterFormData,
            pageIndex: 1,
          });
        },
      });
    }

    if ((conditionFormData.organizations || []).length > 0) {
      chips.push({
        id: "organizations",
        label: `${t("organization_name")} : ${conditionFormData.organizations
          .map((item) => item.name)
          .join(", ")}`,
        onRemove: () => {
          onApplyFilterCondition({
            ...conditionFormData,
            organizations: [],
          });
          onChangeFilterFormData({
            ...filterFormData,
            pageIndex: 1,
          });
        },
      });
    }

    return chips;
  }, [
    conditionFormData,
    currentSortField,
    currentSortOrder,
    filterFormData,
    onApplyFilterCondition,
    onChangeFilterFormData,
    t,
  ]);

  const onApplyHeaderFilter = (columnId: string, value: any[]) => {
    if (columnId === "health_index") {
      const checkedValues = Array.isArray(value) ? value : [];
      const nextHealthIndex = HEALTH_INDEX_OPTIONS.map((opt) => ({
        key: opt.key,
        name: _t(
          `facilityManagementPage.modalFilterCondition.healthIndexLabels.${opt.key}`
        ),
        checked: checkedValues.includes(opt.key),
      })).filter((item) => item.checked);

      onApplyFilterCondition({
        ...conditionFormData,
        healthIndex: nextHealthIndex,
      });
    }

    if (columnId === "internal_labels") {
      const checkedValues = Array.isArray(value) ? value : [];
      const internalLabelsData = internalLabelsResp?.data || [];
      const nextInternalLabels = internalLabelsData
        .filter((label: InternalLabelData) => checkedValues.includes(label.id))
        .map((label: InternalLabelData) => ({ ...label, checked: true }));

      onApplyFilterCondition({
        ...conditionFormData,
        internalLabels: nextInternalLabels,
      });
    }

    if (columnId === "organizations") {
      const checkedValues = Array.isArray(value) ? value : [];
      const organizationsData = organizationsResp?.list || [];
      const nextOrganizations = organizationsData
        .filter((organization: OrganizationData) =>
          checkedValues.includes(organization.id)
        )
        .map((organization: OrganizationData) => ({
          ...organization,
          checked: true,
        }));

      onApplyFilterCondition({
        ...conditionFormData,
        organizations: nextOrganizations,
      });
    }

    if (columnId === "is_archived") {
      const checkedValues = Array.isArray(value) ? value : [];
      const hasArchivedTrue = checkedValues.includes("true");
      const hasArchivedFalse = checkedValues.includes("false");

      onApplyFilterCondition({
        ...conditionFormData,
        isArchived:
          hasArchivedTrue && hasArchivedFalse
            ? undefined
            : hasArchivedTrue
            ? { key: "is_archived", shouldInclude: true }
            : hasArchivedFalse
            ? { key: "is_archived", shouldInclude: false }
            : undefined,
      });
    }

    onChangeFilterFormData({
      ...filterFormData,
      pageIndex: 1,
    });
  };

  const onClearHeaderFilter = (columnId: string) => {
    if (columnId === "health_index") {
      onApplyFilterCondition({
        ...conditionFormData,
        healthIndex: [],
      });
    }

    if (columnId === "internal_labels") {
      onApplyFilterCondition({
        ...conditionFormData,
        internalLabels: [],
      });
    }

    if (columnId === "organizations") {
      onApplyFilterCondition({
        ...conditionFormData,
        organizations: [],
      });
    }

    if (columnId === "is_archived") {
      onApplyFilterCondition({
        ...conditionFormData,
        isArchived: undefined,
      });
    }

    onChangeFilterFormData({
      ...filterFormData,
      pageIndex: 1,
    });
  };

  const getFilterOptionsForColumn = (columnId: string) => {
    if (columnId === "health_index") {
      return HEALTH_INDEX_OPTIONS.map((opt) => ({
        label: _t(
          `facilityManagementPage.modalFilterCondition.healthIndexLabels.${opt.key}`
        ),
        value: opt.key,
        checked: (conditionFormData.healthIndex || []).some(
          (item) => item.checked && item.key === opt.key
        ),
      }));
    }

    if (columnId === "internal_labels") {
      const selected = new Set(
        (conditionFormData.internalLabels || []).map((item) => item.id)
      );
      return (internalLabelsResp?.data || []).map(
        (item: InternalLabelData) => ({
          label: item.name,
          value: item.id,
          checked: selected.has(item.id),
        })
      );
    }

    if (columnId === "organizations") {
      const selected = new Set(
        (conditionFormData.organizations || []).map((item) => item.id)
      );
      return (organizationsResp?.list || []).map((item: OrganizationData) => ({
        label: item.name,
        value: item.id,
        checked: selected.has(item.id),
      }));
    }

    if (columnId === "is_archived") {
      return [
        {
          label: t("archived"),
          value: "true",
          checked: conditionFormData.isArchived?.shouldInclude === true,
        },
        {
          label: t("not_archived"),
          value: "false",
          checked: conditionFormData.isArchived?.shouldInclude === false,
        },
      ];
    }

    return [];
  };

  const renderFilterTrigger = (columnId: ManagementColumnId) => {
    const isFilterable = [
      "organizations",
      "is_archived",
      "health_index",
      "internal_labels",
    ].includes(columnId);

    if (!isFilterable) {
      return null;
    }

    const options = getFilterOptionsForColumn(columnId);
    const hasActiveFilter = options.some(
      (option: { checked: boolean }) => option.checked
    );

    return (
      <div className="filter-trigger-wrap">
        <button
          type="button"
          className={`filter-trigger-btn ${
            hasActiveFilter ? "has-filter" : ""
          }`}
          onClick={(event) => {
            event.stopPropagation();
            setActiveFilterColumn((prev) =>
              prev === columnId ? null : columnId
            );
          }}
          aria-label={`filter-${columnId}`}
        >
          <Funnel className="funnel-icon" size={14} strokeWidth={2} />
        </button>
        {activeFilterColumn === columnId && (
          <ColumnFilterPopover
            columnId={columnId}
            title={getColumnLabelText(columnId)}
            options={options}
            onApply={(checkedValues) =>
              onApplyHeaderFilter(columnId, checkedValues)
            }
            onClear={() => onClearHeaderFilter(columnId)}
            onClose={() => setActiveFilterColumn(null)}
          />
        )}
      </div>
    );
  };

  const toggleColumnVisibility = (columnId: ManagementColumnId) => {
    if (columnId === "name" || columnId === "actions") {
      return;
    }

    setVisibleColumns((prev) => {
      const nextColumns = prev.includes(columnId)
        ? normalizeVisibleColumns(prev.filter((col) => col !== columnId))
        : normalizeVisibleColumns([...prev, columnId]);

      localStorage.setItem(
        VISIBLE_COLUMNS_STORAGE_KEY,
        JSON.stringify(nextColumns)
      );

      return nextColumns;
    });
  };

  const applySort = (field: SortableField, order: SortOrder) => {
    onChangeFilterFormData({
      ...filterFormData,
      sort: field,
      order,
      sortBy: getSortByLabel(field, order),
      pageIndex: 1,
    });
  };

  const onSelectSortField = (columnId: ManagementColumnId) => {
    const field = COLUMN_SORT_FIELD[columnId];
    if (!field) {
      return;
    }

    const nextOrder: SortOrder =
      currentSortField === field && currentSortOrder === "asc" ? "desc" : "asc";

    applySort(field, nextOrder);
  };

  const getColumnLabelText = (columnId: ManagementColumnId) => {
    switch (columnId) {
      case "name":
        return t("facility_name");
      case "organizations":
        return t("organization_name");
      case "has_loc_admins":
        return t("loc_admin_present");
      case "date_signup_completed":
        return t("date_signup_completed");
      case "prefecture":
        return t("prefecture");
      case "city":
        return t("city");
      case "category":
        return t("category");
      case "is_archived":
        return t("is_archived");
      case "health_index":
        return t("health_index");
      case "internal_labels":
        return t("internal_labels");
      case "actions":
        return "操作";
    }
  };

  const createSortButton = (
    columnId: ManagementColumnId,
    labelText: string
  ) => {
    const sortableField = COLUMN_SORT_FIELD[columnId];
    if (!sortableField) {
      return <span className="column-label-text">{labelText}</span>;
    }

    const isActive = currentSortField === sortableField;

    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => onSelectSortField(columnId)}
      >
        <span>{labelText}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            currentSortOrder === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  const renderHeaderColumn = (columnId: ManagementColumnId) => {
    const className = `col-th column-${columnId} ${
      columnId === "name" ? "" : "center"
    }`;

    return (
      <div className={className} key={`header-${columnId}`}>
        <div
          className={`spacing header-cell-inner ${
            columnId === "name" ? "is-name" : ""
          }`}
        >
          {createSortButton(columnId, getColumnLabelText(columnId))}
          {renderFilterTrigger(columnId)}
        </div>
      </div>
    );
  };

  const renderLocationAssignedStatus = (item: any) => {
    if (item.organizations.length > 0) {
      const remainingOrganizations = item.organizations.slice(1);
      const remainingOrganizationNames = remainingOrganizations
        .map((organization: OrganizationData) => organization.name)
        .join(", ");

      return (
        <div className="organization-summary">
          <span className="cell-ellipsis">{item.organizations[0].name}</span>
          <OverflowBadge
            count={remainingOrganizations.length}
            tooltip={remainingOrganizationNames}
          />
        </div>
      );
    }

    if (item.independent_use) {
      return <div className="txt">{t("independent_use")}</div>;
    }

    return <div className="txt red-txt">{t("unassigned")}</div>;
  };

  const handleOpenAssignLabels = (item: LocationData) => {
    setCurrentLocation(item);
    setShowAssignLabelsModal(true);
  };

  const handleCloseAssignLabels = () => {
    setShowAssignLabelsModal(false);
    setCurrentLocation(null);
  };

  const handleUpdateAssignLabels = async (
    addIds: string[],
    deleteIds: string[]
  ) => {
    if (!currentLocation) return;
    const locationId = currentLocation.id;
    await onUpdateAssignLabels(locationId, addIds, deleteIds);
    setShowAssignLabelsModal(false);
    setCurrentLocation(null);
  };

  const handleOpenDeleteAccount = (item: LocationData) => {
    setCurrentLocation(item);
    setShowDeleteAccountModal(true);
  };

  const handleCloseDeleteAccount = () => {
    setShowDeleteAccountModal(false);
    setCurrentLocation(null);
  };

  const handleDeleteComplete = () => {
    // Refresh the list after deletion
    if (props.onDeleteAccountComplete) {
      props.onDeleteAccountComplete();
    }
  };

  return (
    <>
      <div className="white-border-panel">
        <ListTopBar
          conditionFormData={conditionFormData}
          filterFormData={filterFormData}
          showFilterButton={false}
          showSortDropdown={false}
          showCurrentFilters={false}
          onClickFilter={() => {}}
          onClickClearFilterCondition={() => {
            onClickClearFilterCondition();
          }}
          onChangeFilterFormData={(filterFormData: any) => {
            onChangeFilterFormData(filterFormData);
          }}
        />

        <ConfigurableTableLayout
          className="facility-management-table-layout"
          onContainerClick={() => {
            setShowColumnSettings(false);
            setActiveFilterColumn(null);
            setOpenActionMenuId(null);
          }}
          controls={
            <div
              className="column-settings-container"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="btn-column-settings"
                onClick={() => setShowColumnSettings((prev) => !prev)}
              >
                表示項目の設定 ⚙️
              </button>
              {showColumnSettings && (
                <div className="column-settings-dropdown">
                  <div className="dropdown-title">表示列の切り替え</div>
                  {ALL_COLUMNS.map((colId) => (
                    <label key={`setting-${colId}`} className="setting-label">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(colId)}
                        disabled={colId === "name" || colId === "actions"}
                        onChange={() => toggleColumnVisibility(colId)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="label-text">
                        {getColumnLabelText(colId)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          }
          conditionChips={activeConditionChips}
          onClearAllConditions={onClickClearFilterCondition}
          clearAllLabel={t("clear_condition")}
          tableContent={
            <div
              className={`table-data table-data-grid table-data-grid-pc-only facility-management-list ${visibleColumns
                .map((columnId) => `show-${columnId}`)
                .join(" ")}`}
            >
              <div className="row-th refactored-facility-list-header">
                {visibleColumns.map((columnId) => renderHeaderColumn(columnId))}
              </div>
              {dataList
                .slice(0, filterFormData.numberPerPage)
                .map((item: LocationData, indexFacility: number) => (
                  <div className="row-td" key={indexFacility}>
                    {visibleColumns.includes("name") && (
                      <div className="col-td column-name">
                        <div className="spacing">
                          {item.is_demo && <DemoBadge type="facility" />}
                          <FacilityIssuesIcon facility={item} />
                          <a
                            href={`/location/${item.id}`}
                            className="blue-txt facility-name-link"
                            title={item.name || "-"}
                          >
                            <span className="cell-ellipsis">
                              {item.name || "-"}
                            </span>
                          </a>
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("organizations") && (
                      <div className="col-td center column-organizations">
                        <div className="spacing">
                          {renderLocationAssignedStatus(item)}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("has_loc_admins") && (
                      <div className="col-td center column-has_loc_admins">
                        <div className="spacing">
                          {item?.has_loc_admins ? "True" : "False"}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("date_signup_completed") && (
                      <div className="col-td center column-date_signup_completed">
                        <div className="spacing">
                          {calculateSignupDate(item).format("YYYY-MM-DD")}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("prefecture") && (
                      <div className="col-td column-prefecture">
                        <div className="spacing">
                          <div
                            className="txt cell-ellipsis"
                            title={item.prefecture || "-"}
                          >
                            {item.prefecture || "-"}
                          </div>
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("city") && (
                      <div className="col-td column-city">
                        <div className="spacing">
                          <div
                            className="txt cell-ellipsis"
                            title={item.city || "-"}
                          >
                            {item.city || "-"}
                          </div>
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("category") && (
                      <div className="col-td center column-category">
                        <div className="spacing">
                          {item.categories.length > 0 ? (
                            <div className="category-summary">
                              <span className="value-chip cell-ellipsis">
                                {item.categories[0].name}
                              </span>
                              <OverflowBadge
                                count={item.categories.length - 1}
                                tooltip={item.categories
                                  .slice(1)
                                  .map((category: CategoryData) => category.name)
                                  .join(", ")}
                              />
                            </div>
                          ) : (
                            "-"
                          )}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("is_archived") && (
                      <div className="col-td center column-is_archived">
                        <div className="spacing">
                          {item.is_archived ? "True" : "False"}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("health_index") && (
                      <div className="col-td center column-health_index">
                        <div className="spacing">
                          <ScoreItem score={item.stat.score} />
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("internal_labels") && (
                      <div className="col-td center column-internal_labels">
                        <div className="spacing">
                          {item.internal_labels &&
                          item.internal_labels.length > 0 ? (
                            <div className="internal-labels-summary">
                              <span className="label-chip cell-ellipsis">
                                {item.internal_labels[0].name}
                              </span>
                              <OverflowBadge
                                count={item.internal_labels.length - 1}
                                tooltip={item.internal_labels
                                  .slice(1)
                                  .map((label: InternalLabelData) => label.name)
                                  .join(", ")}
                              />
                            </div>
                          ) : (
                            "-"
                          )}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("actions") && (
                      <div className="col-td center buttons-section column-actions">
                        <TableRowActions
                          actions={
                            [
                              {
                                key: "see_screen",
                                label: t("see_screen"),
                                icon: ExternalLink,
                                href: `/location/${item.id}`,
                              },
                              {
                                key: "assign_org",
                                label: t("assign_org"),
                                icon: Building2,
                                onClick: () => onClickAssignOrg(item),
                              },
                              {
                                key: "assign_internal_labels",
                                label: t("assign_internal_labels"),
                                icon: Tags,
                                onClick: () => handleOpenAssignLabels(item),
                              },
                              {
                                key: "delete_account",
                                label: t("delete_account"),
                                icon: Trash2,
                                isDanger: true,
                                onClick: () => handleOpenDeleteAccount(item),
                              },
                            ] as TableRowActionItem[]
                          }
                          isMenuOpen={openActionMenuId === item.id}
                          onToggleMenu={() =>
                            setOpenActionMenuId((currentId) =>
                              currentId === item.id ? null : item.id
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                ))}
            </div>
          }
          pagination={{
            filterFormData,
            totalRowsNumber: totalCount,
            onChangeFilterFormData: (nextFilterFormData: any) => {
              onChangeFilterFormData(nextFilterFormData);
            },
          }}
        />
      </div>

      {showAssignLabelsModal && currentLocation && (
        <ModalAssignInternalLabels
          locationData={currentLocation}
          onClose={handleCloseAssignLabels}
          onUpdateAssignLabels={handleUpdateAssignLabels}
        />
      )}

      {showDeleteAccountModal && currentLocation && (
        <ModalDeleteAccount
          locationData={currentLocation}
          onClose={handleCloseDeleteAccount}
          onDeleteComplete={handleDeleteComplete}
        />
      )}
    </>
  );
};

export default FacilityManagementList;
